import React, { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import API_BASE from '../../utils/api.js';
import notify from '../../utils/notify';
import useProjects from '../../hooks/useProjects';
import { INTERNAL_PROJECT_ID, DEFAULT_TIME_CATEGORIES } from '../../utils/timeBillingShared';
import '../../component-styles/TimerWidget.css';

const ACTIVITY_PRESETS = ['Meeting', 'Procurement', 'Sourcing', 'Design'];

function formatElapsed(startedAt) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

export default function TimerWidget() {
  const apiUrl = API_BASE;
  const userId = localStorage.getItem('userId');

  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [elapsedLabel, setElapsedLabel] = useState('00:00:00');
  const [busy, setBusy] = useState(false);
  const [activities, setActivities] = useState(ACTIVITY_PRESETS);
  const [categories, setCategories] = useState(DEFAULT_TIME_CATEGORIES);
  const [form, setForm] = useState({
    projectId: '',
    category: '',
    activity: '',
    description: '',
  });

  const wrapRef = useRef(null);
  const { projects } = useProjects({ activeOnly: true, isOpen: open });

  useEffect(() => {
    if (!userId) return;
    fetch(`${apiUrl}/api/timebilling-timer/`)
      .then((res) => res.json())
      .then((data) => setTimer(data.timer || null))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [apiUrl, userId]);

  useEffect(() => {
    if (!timer) return undefined;
    setElapsedLabel(formatElapsed(timer.startedAt));
    const interval = setInterval(() => setElapsedLabel(formatElapsed(timer.startedAt)), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  useEffect(() => {
    if (!open || !userId) return;
    fetch(`${apiUrl}/api/settings/${userId}`)
      .then((res) => res.json())
      .then((data) => {
        const storedActivities = Array.isArray(data.timeActivities)
          ? data.timeActivities.map((a) => (typeof a === 'string' ? a.trim() : '')).filter(Boolean)
          : [];
        setActivities(Array.from(new Set([...ACTIVITY_PRESETS, ...storedActivities])));
        const storedCategories = Array.isArray(data.timeCategories)
          ? data.timeCategories.map((c) => (typeof c === 'string' ? c.trim() : '')).filter(Boolean)
          : [];
        setCategories(Array.from(new Set([...DEFAULT_TIME_CATEGORIES, ...storedCategories])));
      })
      .catch(() => {});
  }, [open, userId, apiUrl]);

  useEffect(() => {
    function handleOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  if (!userId || !loaded) return null;

  async function handleStart() {
    if (!form.projectId) {
      notify('Choose a project (or Internal / Non-Project) to start the timer.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/timebilling-timer/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.status === 409) {
        notify('A timer is already running.');
        return;
      }
      if (!res.ok) throw new Error('start_failed');
      const data = await res.json();
      setTimer(data.timer);
    } catch (err) {
      console.error('Failed to start timer:', err);
      notify('Failed to start timer');
    } finally {
      setBusy(false);
    }
  }

  async function handleStop() {
    setBusy(true);
    try {
      const res = await fetch(`${apiUrl}/api/timebilling-timer/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: timer?.description }),
      });
      if (!res.ok) throw new Error('stop_failed');
      document.dispatchEvent(new Event('timebilling-updated'));
      setTimer(null);
      setForm({ projectId: '', category: '', activity: '', description: '' });
      notify.success('Time entry logged');
      setOpen(false);
    } catch (err) {
      console.error('Failed to stop timer:', err);
      notify('Failed to stop timer');
    } finally {
      setBusy(false);
    }
  }

  async function handleDiscard() {
    if (!window.confirm('Discard this running timer without saving a time entry?')) return;
    setBusy(true);
    try {
      await fetch(`${apiUrl}/api/timebilling-timer/`, { method: 'DELETE' });
      setTimer(null);
      setOpen(false);
    } catch (err) {
      console.error('Failed to discard timer:', err);
      notify('Failed to discard timer');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tw-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`tw-btn ${timer ? 'running' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-label="Time tracker"
      >
        <Clock size={19} />
        {timer && <span className="tw-elapsed-pill">{elapsedLabel}</span>}
      </button>

      {open && (
        <div className="tw-dropdown">
          {timer ? (
            <>
              <div className="tw-running-header">
                <span className="tw-running-dot" />
                Timer running — {elapsedLabel}
              </div>
              <textarea
                placeholder="What are you working on?"
                value={timer.description || ''}
                onChange={(e) => setTimer((t) => ({ ...t, description: e.target.value }))}
              />
              <div className="tw-actions">
                <button type="button" className="tw-discard" onClick={handleDiscard} disabled={busy}>
                  Discard
                </button>
                <button type="button" className="tw-stop" onClick={handleStop} disabled={busy}>
                  Stop &amp; Save
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="tw-select-wrap">
                <select
                  value={form.projectId}
                  onChange={(e) => setForm((f) => ({ ...f, projectId: e.target.value }))}
                >
                  <option value="">Select project...</option>
                  <option value={INTERNAL_PROJECT_ID}>Internal / Non-Project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              {form.projectId === INTERNAL_PROJECT_ID && (
                <div className="tw-select-wrap">
                  <select
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    <option value="">Select category...</option>
                    {categories.map((c, i) => (
                      <option key={i} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="tw-select-wrap">
                <select
                  value={form.activity}
                  onChange={(e) => setForm((f) => ({ ...f, activity: e.target.value }))}
                >
                  <option value="">Select activity...</option>
                  {activities.map((a, i) => (
                    <option key={i} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>

              <textarea
                placeholder="What are you working on? (optional)"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />

              <div className="tw-actions">
                <button type="button" className="tw-start" onClick={handleStart} disabled={busy}>
                  Start Timer
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
